"use client";

import { useEffect, useMemo, useState } from "react";
import { subDays } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePostAggregatePizzas } from "@/api/pizza-checker/pizzas/pizzas";
import { useBranch } from "@/context/branch-context";
import { useDashboardDateRange } from "@/context/dashboard-date-range-context";
import { cn } from "@/lib/utils";
import {
  buildScorePipeline,
  calculateScore,
  SCORE_PENALTIES,
} from "@/lib/branch-score";
import { useLocalization } from "@/context/localization-context";
import { ArrowUpDown } from "lucide-react";

type BranchScoreAggregate = {
  _id?: unknown;
  totalImages?: unknown;
  totalPenalty?: unknown;
  burnedCount?: unknown;
  undercookedCount?: unknown;
  missingIngredientCount?: unknown;
  extraIngredientCount?: unknown;
  missingPizzaSaverCount?: unknown;
  invalidPhotoCount?: unknown;
  noPizzaCount?: unknown;
};

type BranchScoreRow = {
  branchId: string;
  branchName: string;
  totalImages: number;
  totalPenalty: number;
  burnedCount: number;
  undercookedCount: number;
  missingIngredientCount: number;
  extraIngredientCount: number;
  missingPizzaSaverCount: number;
  invalidPhotoCount: number;
  noPizzaCount: number;
  score: number | null;
};

type SortKey =
  | "branchName"
  | "score"
  | "totalImages"
  | "totalPenalty"
  | "burnedCount"
  | "undercookedCount"
  | "missingIngredientCount"
  | "extraIngredientCount"
  | "missingPizzaSaverCount"
  | "invalidPhotoCount"
  | "noPizzaCount";

type SortDirection = "asc" | "desc";

type SortState = {
  key: SortKey;
  direction: SortDirection;
};

const readNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

const getScoreBadgeClass = (score: number | null) => {
  if (score === null) {
    return "bg-muted text-muted-foreground border border-border";
  }

  if (score >= 95) {
    return "bg-green-500/15 text-green-400 border border-green-500/30";
  }

  if (score >= 85) {
    return "bg-yellow-500/15 text-yellow-400 border border-yellow-500/30";
  }

  return "bg-red-500/15 text-red-400 border border-red-500/30";
};

const getSortValue = (row: BranchScoreRow, key: SortKey) => {
  if (key === "branchName") {
    return row.branchName;
  }

  if (key === "score") {
    return row.score ?? Number.NEGATIVE_INFINITY;
  }

  return row[key];
};

function SortableTableHead({
  sortKey,
  sortState,
  onSort,
  children,
  className,
}: {
  sortKey: SortKey;
  sortState: SortState;
  onSort: (key: SortKey) => void;
  children: React.ReactNode;
  className?: string;
}) {
  const isActive = sortState.key === sortKey;

  return (
    <TableHead className={className}>
      <Button
        variant="ghost"
        className={cn(
          "h-8 px-2 text-xs font-medium text-muted-foreground",
          className?.includes("text-right") && "ml-auto",
        )}
        onClick={() => onSort(sortKey)}
      >
        {children}
        <ArrowUpDown
          className={cn("ml-1 h-3.5 w-3.5", isActive && "text-foreground")}
        />
      </Button>
    </TableHead>
  );
}

export function BranchQualityScoring({ className }: { className?: string }) {
  const { branches } = useBranch();
  const { processedDateRange } = useDashboardDateRange();
  const { mutateAsync, isPending } = usePostAggregatePizzas({});
  const { t, formatNumber } = useLocalization();
  const [aggregateRows, setAggregateRows] = useState<BranchScoreAggregate[]>(
    [],
  );
  const [sortState, setSortState] = useState<SortState>({
    key: "score",
    direction: "desc",
  });

  const { start, end } = useMemo(() => {
    if (processedDateRange.start && processedDateRange.end) {
      return { start: processedDateRange.start, end: processedDateRange.end };
    }

    const endDate = new Date();
    return { start: subDays(endDate, 7), end: endDate };
  }, [processedDateRange]);

  useEffect(() => {
    let isMounted = true;

    async function fetchBranchScores() {
      const pipeline = buildScorePipeline(start, end, {}, "$branch_id");

      try {
        const response = await mutateAsync({ data: { pipeline } });

        if (isMounted) {
          setAggregateRows(response.results as BranchScoreAggregate[]);
        }
      } catch (error) {
        console.error("Failed to fetch branch quality scores", error);
        if (isMounted) {
          setAggregateRows([]);
        }
      }
    }

    fetchBranchScores();

    return () => {
      isMounted = false;
    };
  }, [end, mutateAsync, start]);

  const scoreRows = useMemo<BranchScoreRow[]>(() => {
    const aggregatesByBranch = new Map(
      aggregateRows
        .filter((row) => typeof row._id === "string")
        .map((row) => [row._id as string, row]),
    );

    return (branches ?? [])
      .filter((branch) => branch.bb_branch_id)
      .map((branch) => {
        const aggregate = aggregatesByBranch.get(branch.bb_branch_id);
        const totalImages = readNumber(aggregate?.totalImages);
        const totalPenalty = readNumber(aggregate?.totalPenalty);
        const score = calculateScore(totalImages, totalPenalty);

        return {
          branchId: branch.bb_branch_id,
          branchName: branch.name,
          totalImages,
          totalPenalty,
          burnedCount: readNumber(aggregate?.burnedCount),
          undercookedCount: readNumber(aggregate?.undercookedCount),
          missingIngredientCount: readNumber(
            aggregate?.missingIngredientCount,
          ),
          extraIngredientCount: readNumber(aggregate?.extraIngredientCount),
          missingPizzaSaverCount: readNumber(
            aggregate?.missingPizzaSaverCount,
          ),
          invalidPhotoCount: readNumber(aggregate?.invalidPhotoCount),
          noPizzaCount: readNumber(aggregate?.noPizzaCount),
          score,
        };
      })
      .sort((a, b) => {
        if (a.score === null && b.score === null) {
          return a.branchName.localeCompare(b.branchName);
        }

        if (a.score === null) {
          return 1;
        }

        if (b.score === null) {
          return -1;
        }

        return b.score - a.score;
      });
  }, [aggregateRows, branches]);

  const sortedBreakdownRows = useMemo(() => {
    return [...scoreRows].sort((a, b) => {
      const aValue = getSortValue(a, sortState.key);
      const bValue = getSortValue(b, sortState.key);

      const comparison =
        typeof aValue === "string" && typeof bValue === "string"
          ? aValue.localeCompare(bValue)
          : Number(aValue) - Number(bValue);

      return sortState.direction === "asc" ? comparison : -comparison;
    });
  }, [scoreRows, sortState]);

  const formatScore = (score: number | null) =>
    score === null
      ? t("common.noData")
      : formatNumber(score / 100, {
          style: "percent",
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        });

  const handleSort = (key: SortKey) => {
    setSortState((current) => ({
      key,
      direction:
        current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  return (
    <Card className={cn("w-full", className)}>
      <CardHeader>
        <CardTitle>{t("dashboard.qualityScoring.title")}</CardTitle>
        <CardDescription>
          {t("dashboard.qualityScoring.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  sortKey="branchName"
                  sortState={sortState}
                  onSort={handleSort}
                >
                  {t("common.branch")}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="score"
                  sortState={sortState}
                  onSort={handleSort}
                >
                  {t("common.score")}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="totalImages"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.photos")}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="totalPenalty"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("stats.penaltyPoints")}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="burnedCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.burnt", {
                    points: SCORE_PENALTIES.burnedCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="undercookedCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.undercooked", {
                    points: SCORE_PENALTIES.undercookedCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="missingIngredientCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.missingIngredients", {
                    points: SCORE_PENALTIES.missingIngredientCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="extraIngredientCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.notRemoved", {
                    points: SCORE_PENALTIES.extraIngredientCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="missingPizzaSaverCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.missingPizzaSaver", {
                    points: SCORE_PENALTIES.missingPizzaSaverCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="invalidPhotoCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.invalidPhoto", {
                    points: SCORE_PENALTIES.invalidPhotoCount,
                  })}
                </SortableTableHead>
                <SortableTableHead
                  sortKey="noPizzaCount"
                  sortState={sortState}
                  onSort={handleSort}
                  className="text-right"
                >
                  {t("dashboard.qualityScoring.noPizza", {
                    points: SCORE_PENALTIES.noPizzaCount,
                  })}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedBreakdownRows.map((row) => (
                <TableRow key={row.branchId}>
                  <TableCell className="min-w-48 font-medium">
                    {row.branchName}
                  </TableCell>
                  <TableCell className="min-w-44">
                    <div className="flex flex-col gap-2">
                      <Badge
                        className={cn(
                          "w-fit text-sm",
                          getScoreBadgeClass(row.score),
                        )}
                      >
                        {formatScore(row.score)}
                      </Badge>
                      <Progress
                        value={row.score === null ? 0 : Math.max(row.score, 0)}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.totalImages)}
                  </TableCell>
                  <TableCell className="text-right font-medium tabular-nums">
                    {formatNumber(row.totalPenalty)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.burnedCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.undercookedCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.missingIngredientCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.extraIngredientCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.missingPizzaSaverCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.invalidPhotoCount)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatNumber(row.noPizzaCount)}
                  </TableCell>
                </TableRow>
              ))}

              {sortedBreakdownRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={11}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {isPending
                      ? t("dashboard.qualityScoring.loading")
                      : t("dashboard.qualityScoring.noBranches")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
